import type { UseBaseQueryOptions } from '@tanstack/react-query'

import api from 'src/constants/api'

import useAccount from 'src/hooks/utils/useAccount'
import useQuery from 'src/hooks/utils/useQuery'

import type { Emission } from './useEmissionsQuery'

export interface ExportEmission extends Emission {
  // Export only fields
  resourceGroup?: string
  resourceId?: string
  itemName?: string
}

export interface EmissionsQueryInput {
  subscriptionIds: string[]
  months?: number
}

const getEmissionsExportQueryKeys = (accountId: string, input: EmissionsQueryInput) => {
  return ['useEmissionsExportQuery', accountId, input]
}

const fetchEmissionsExport = async (accountId: string, input: EmissionsQueryInput, signal?: AbortSignal) => {
  const response = await api.post(`/sustainability/account/${accountId}/emissions/export`, input, { signal })
  return response.data as ExportEmission[]
}

const useEmissionsQuery = (input: EmissionsQueryInput, options?: UseBaseQueryOptions<ExportEmission[]>) => {
  const account = useAccount()

  const emissionsQueryResult = useQuery(
    getEmissionsExportQueryKeys(account.id, input),
    async ({ signal }) => {
      const data = await fetchEmissionsExport(account.id, input, signal)
      return data
    },
    {
      enabled: input.subscriptionIds.length > 0 && !!account.id,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      ...options
    }
  )

  return {
    ...emissionsQueryResult,
    data: emissionsQueryResult.data?.map((emission) => ({
      ...emission,
      scope1: emission.scope1 ?? 0,
      scope2: emission.scope2 ?? 0,
      scope3: emission.scope3 ?? 0
    }))
  }
}

export default useEmissionsQuery
